import { ComponentProps, createContext, ReactNode, useContext, useState } from 'react';
import { AlertDialog } from '@/components/UI';
import { Icon } from 'vopay';

type AlertDialogProps = ComponentProps<typeof AlertDialog>;
type AlertDialogCloseType = 'confirm' | 'abort' | 'close';
type AlertDialogOptions = Pick<AlertDialogProps, 'abortLabel' | 'confirmLabel' | 'top'>;

type AlertDialogMethod = (
  title: ReactNode,
  content: ReactNode,
  options?: AlertDialogOptions,
) => Promise<AlertDialogCloseType>;

type AlertDialogCtx = {
  open: AlertDialogMethod;
  success: AlertDialogMethod;
  caution: AlertDialogMethod;
  error: AlertDialogMethod;
  close: () => void;
};

const AlertDialogContext = createContext<AlertDialogCtx>({
  open: () => {
    console.info('open not implemented');

    return Promise.resolve('abort');
  },
  success: () => {
    console.info('success not implemented');

    return Promise.resolve('abort');
  },
  caution: () => {
    console.info('caution not implemented');

    return Promise.resolve('abort');
  },
  error: () => {
    console.info('error not implemented');

    return Promise.resolve('abort');
  },
  close: () => {
    console.info('close not implemented');

    return Promise.resolve('abort');
  },
});

export const AlertDialogProvider = ({ children }: { children: ReactNode }) => {
  const [props, setProps] = useState<AlertDialogProps>({
    isOpen: false,
    title: '',
    content: '',
    onClose: handleClose,
  });

  function handleClose() {
    setProps((prev) => ({ ...prev, isOpen: false }));
  }

  const open: AlertDialogMethod = (title, content, options) => {
    return new Promise<AlertDialogCloseType>((res) => {
      setProps({
        isOpen: true,
        title,
        content,
        ...createEventHandlers(res),
        ...options,
      });
    });
  };

  const success: AlertDialogMethod = (title, content, options) => {
    return new Promise<AlertDialogCloseType>((res) => {
      setProps({
        isOpen: true,
        title,
        content,
        top: <Icon icon="dialogSuccess" size="jumbo" width="104px" height="104px" color="none" />,
        ...createEventHandlers(res),
        ...options,
      });
    });
  };

  const caution: AlertDialogMethod = (title, content, options) => {
    return new Promise<AlertDialogCloseType>((res) => {
      setProps({
        isOpen: true,
        title,
        content,
        top: <Icon icon="cautionary2" size="jumbo" width="104px" height="104px" color="none" />,
        ...createEventHandlers(res),
        ...options,
      });
    });
  };

  const error: AlertDialogMethod = (title, content, options) => {
    return new Promise<AlertDialogCloseType>((res) => {
      setProps({
        isOpen: true,
        title,
        content,
        // just in case we need it in the future
        // left: <MaterialSymbol icon="warning" family="rounded" size="90px" color="goldenrod" />,
        ...createEventHandlers(res),
        ...options,
      });
    });
  };

  function createEventHandlers(res: (val: AlertDialogCloseType) => void) {
    return {
      onClose: () => {
        res('close');
        handleClose();
      },
      onAbort: () => {
        res('abort');
        handleClose();
      },
      onConfirm: () => {
        res('confirm');
        handleClose();
      },
    };
  }

  return (
    <AlertDialogContext.Provider
      value={{
        open,
        success,
        caution,
        error,
        close: handleClose,
      }}
    >
      {children}
      <AlertDialog {...props} />
    </AlertDialogContext.Provider>
  );
};

export const useAlertDialog = () => useContext(AlertDialogContext);
